<template>
    <div ref="container" class="w-full h-full">
        <canvas
            ref="canvas"
            class="block w-full h-full touch-pan-y cursor-grab active:cursor-grabbing"
            @pointerdown="hintVisible = hintPending = false"
        ></canvas>
        <transition leave-active-class="transition-opacity duration-500" leave-to-class="opacity-0">
            <div
                v-if="hintVisible"
                data-html2canvas-ignore
                class="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/50 text-white text-center px-8 pointer-events-none"
            >
                <!-- inline svg: the remixicon font is a subset without a gesture glyph -->
                <svg class="hint-hand w-12 h-12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="12" cy="12" r="5" />
                    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.5" />
                </svg>
                <p class="max-w-xs text-sm font-Inter-Semibold">{{ touchHint }}</p>
            </div>
        </transition>
    </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { frameRings } from './frameRings.cjs';
import { fontFromTypo } from '@/components/Editor/StarMap/woodMasks';

// Scene units are millimetres of the real object.
const PLY = 3;
// stacked alpha-tested planes give a cut layer its thickness; the lower ones show the charred laser edge
const SLICES = 6;
const EXPLODE_GAP = 40;
const PIN_HEIGHT = 36;
const PIN_RADIUS = 10;
const PIN_LABEL_WIDTH = 50;
const WALL_COLOR = '#ebe8e3';
const GRAIN_MM = 80;
const GRAIN_PX = 1024;
const GRAIN_BUMP = 0.2;
const ENV_INTENSITY = 0.25;
// growth lines per texture tile
const GRAIN_RINGS = 14;
const grainCache = new Map();
// each ply is cut from a different spot of the board, so its grain doesn't line up with the one behind
const grainShift = (index) => [(index * 0.618) % 1, (index * 0.382) % 1];

// smooth value noise that repeats every `period` units, so the grain tiles seamlessly
function periodicNoise(period) {
    const values = Array.from({ length: period }, Math.random);
    return (x) => {
        const i = Math.floor(x);
        const f = x - i;
        const a = values[((i % period) + period) % period];
        const b = values[(((i + 1) % period) + period) % period];
        return a + (b - a) * f * f * (3 - 2 * f);
    };
}

// built once per colour and cached, so the per-pixel loop never runs while orbiting
function woodCanvas(color) {
    const cached = grainCache.get(color);
    if (cached) return cached;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = GRAIN_PX;
    const ctx = canvas.getContext('2d');
    const image = ctx.createImageData(GRAIN_PX, GRAIN_PX);
    const n = parseInt(color.slice(1), 16);
    const [r, g, b] = [n >> 16, (n >> 8) & 255, n & 255];
    const drift = periodicNoise(4);
    const wobble = periodicNoise(16);
    const spacing = periodicNoise(8);
    const fibre = periodicNoise(512);
    const fibreBreak = periodicNoise(64);
    const tone = periodicNoise(3);
    for (let y = 0; y < GRAIN_PX; y++) {
        const v = y / GRAIN_PX;
        for (let x = 0; x < GRAIN_PX; x++) {
            const u = x / GRAIN_PX;
            // grain lines meander along their length instead of running dead straight
            let w = v + 0.05 * drift(u * 4) + 0.008 * wobble(u * 16);
            w -= Math.floor(w);
            const t = GRAIN_RINGS * w + 2 * spacing(w * 8);
            const ring = Math.pow(0.5 + 0.5 * Math.cos(2 * Math.PI * t), 14);
            const streak = fibre(w * 512) * (0.5 + fibreBreak(u * 64 + w * 64));
            const shade = 1 - 0.05 * ring - 0.045 * streak + 0.03 * (tone(u * 3) - 0.5);
            // darker latewood is warmer, not just dimmer
            const i = (y * GRAIN_PX + x) * 4;
            image.data[i] = r * shade;
            image.data[i + 1] = g * Math.pow(shade, 1.15);
            image.data[i + 2] = b * Math.pow(shade, 1.35);
            image.data[i + 3] = 255;
        }
    }
    ctx.putImageData(image, 0, 0);
    grainCache.set(color, canvas);
    return canvas;
}

// rounded-corner slab centred like a BoxGeometry; group 0 = front/back faces (UVs 0..1), group 1 = cut edge
function roundedPlate(width, height, depth, radius) {
    const x = -width / 2;
    const y = -height / 2;
    const shape = new THREE.Shape()
        .moveTo(x + radius, y)
        .lineTo(x + width - radius, y)
        .quadraticCurveTo(x + width, y, x + width, y + radius)
        .lineTo(x + width, y + height - radius)
        .quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
        .lineTo(x + radius, y + height)
        .quadraticCurveTo(x, y + height, x, y + height - radius)
        .lineTo(x, y + radius)
        .quadraticCurveTo(x, y, x + radius, y);
    const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: 6 });
    geometry.translate(0, 0, -depth / 2);
    // cap UVs come out in millimetres; the engraved face texture wants 0..1
    const { position, uv } = geometry.attributes;
    const caps = geometry.groups[0];
    for (let i = caps.start; i < caps.start + caps.count; i++) {
        uv.setXY(i, (position.getX(i) - x) / width, (position.getY(i) - y) / height);
    }
    return geometry;
}

// clones share one Source, so the backplate, frame rails and plies upload one 1024² texture per colour, not one each
const grainTextures = new Map();
function woodTexture(color, anisotropy) {
    let texture = grainTextures.get(color);
    if (!texture) {
        texture = new THREE.CanvasTexture(woodCanvas(color));
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
        grainTextures.set(color, texture);
    }
    const clone = texture.clone();
    clone.anisotropy = anisotropy || 1;
    return clone;
}

// laser-cut plywood chars to a near-black brown, not a dimmer copy of the face
const CHAR_COLOR = '#1a100a';
const charred = (color) => new THREE.Color(color).lerp(new THREE.Color(CHAR_COLOR), 0.9);

// masks are opaque white/black; canvas compositing needs white-on-transparent
function woodAlpha(mask) {
    const canvas = document.createElement('canvas');
    canvas.width = mask.width;
    canvas.height = mask.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(mask, 0, 0);
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = image.data;
    for (let i = 0; i < data.length; i += 4) {
        data[i + 3] = data[i];
        data[i] = data[i + 1] = data[i + 2] = 255;
    }
    ctx.putImageData(image, 0, 0);
    return canvas;
}

function holesOf(wood) {
    const canvas = document.createElement('canvas');
    canvas.width = wood.width;
    canvas.height = wood.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(wood, 0, 0);
    return canvas;
}

// grow the dark wall into the hole so the char reads when the camera looks into a cut
function growMask(mask, pixels) {
    if (pixels <= 0) return mask;
    const canvas = document.createElement('canvas');
    canvas.width = mask.width;
    canvas.height = mask.height;
    const ctx = canvas.getContext('2d');
    ctx.filter = `blur(${pixels}px)`;
    ctx.drawImage(mask, 0, 0);
    ctx.filter = 'none';
    ctx.drawImage(mask, 0, 0);
    return canvas;
}

// scorched band on the face next to every cut
function burnRim(mask, pixels, holes) {
    const canvas = document.createElement('canvas');
    canvas.width = mask.width;
    canvas.height = mask.height;
    const ctx = canvas.getContext('2d');
    ctx.filter = `blur(${pixels}px)`;
    ctx.drawImage(holes, 0, 0);
    ctx.filter = 'none';
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0);
    return canvas;
}

// masks are opaque white (wood) / black canvases
function woodAtCentre(mask) {
    const pixel = mask.getContext('2d').getImageData(mask.width >> 1, mask.height >> 1, 1, 1).data;
    return pixel[0] > 127 && pixel[3] > 127;
}

function lightness(hex) {
    const n = parseInt(hex.slice(1), 16);
    return ((n >> 16) * 0.3 + ((n >> 8) & 255) * 0.59 + (n & 255) * 0.11) / 255;
}

// `mask` (white marks on transparent) filled with one colour
function solidCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    return canvas;
}

function tintedMask(mask, color) {
    const canvas = document.createElement('canvas');
    canvas.width = mask.width;
    canvas.height = mask.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0);
    return canvas;
}

// Interactive 3D model of a layered wooden map: backplate, one or two laser-cut layers, optional overlay ply,
// frame and engraved label. `top` / `middle` are the mask canvases of the cut layers (white = wood), e.g. roads
// over land; `middle` is optional. `overlay` is a further cut ply in front of `top` (e.g. letter shapes).
// `engraving` (white marks on transparent, the board's aspect) is engraved into the top layer.
export default {
    name: 'WoodMapPreview3d',
    props: {
        // shown briefly on touch screens; text comes from the page because the admin mounts this without i18n
        touchHint: { type: String, default: '' },
        width: { type: Number, required: true },
        height: { type: Number, required: true },
        // mask canvases, untyped because HTMLCanvasElement doesn't exist during SSR
        top: { default: null },
        middle: { default: null },
        overlay: { default: null },
        engraving: { default: null },
        // white marks on transparent, engraved into the `middle` ply (e.g. minor roads)
        middleEngraving: { default: null },
        // white marks on transparent, engraved into the backplate where the cut layers are open
        backplateEngraving: { default: null },
        // stacked cut plies bottom→top: [{ mask, color, engraving?, edge? }]. when set, replaces top/middle/overlay
        cuts: { type: Array, default: null },
        sliceCount: { type: Number, default: SLICES },
        explodeGap: { type: Number, default: EXPLODE_GAP },
        woods: { type: Object, required: true },
        title: { type: String, default: '' },
        subtitle: { type: String, default: '' },
        // FontFamilySelector option for the title; null keeps the site font
        titleTypo: { type: Object, default: null },
        // TextSizeToggle multiplier; grows the badge with the text
        titleSize: { type: Number, default: 1 },
        exploded: { type: Boolean, default: false },
        labelOnFrame: { type: Boolean, default: false },
        // { text } stands a flat-cut pin on the board centre with an engraved plaque below it; null hides it
        pin: { type: Object, default: null },
    },
    data() {
        return { hintVisible: false };
    },
    computed: {
        // canvas ignores the typo's CSS text-transform
        titleText() {
            return this.typoCase(this.title);
        },
    },
    watch: {
        width: 'reframe',
        height: 'reframe',
        top: 'build',
        middle: 'build',
        middleEngraving: 'build',
        overlay: 'build',
        engraving: 'build',
        backplateEngraving: 'build',
        labelOnFrame: 'build',
        pin: { handler: 'build', deep: true },
        cuts: {
            handler(next, prev) {
                this.build();
                if ((!prev || !prev.length) && next && next.length) this.resetCamera();
            },
        },
        woods: { handler: 'build', deep: true },
        title: 'build',
        subtitle: 'build',
        titleTypo: 'build',
        titleSize: 'build',
    },
    async mounted() {
        const mobile = window.matchMedia('(max-width: 1023.98px)').matches;
        const renderer = new THREE.WebGLRenderer({
            canvas: this.$refs.canvas,
            antialias: !mobile,
            powerPreference: mobile ? 'default' : 'high-performance',
            // html2canvas reads the canvas for the order image
            preserveDrawingBuffer: true,
        });
        renderer.setPixelRatio(1);
        renderer.shadowMap.enabled = true;
        // PCF honours shadow.radius for a wider, room-light penumbra; PCFSoft ignores it
        renderer.shadowMap.type = THREE.PCFShadowMap;
        // the light never moves relative to the model: re-render shadows only when the model changes
        renderer.shadowMap.autoUpdate = false;
        this.renderer = renderer;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(WALL_COLOR);
        // soft room reflections instead of flat fill light; baked once, free per frame
        const pmrem = new THREE.PMREMGenerator(renderer);
        const room = new RoomEnvironment();
        this.scene.environment = pmrem.fromScene(room, 0.04).texture;
        room.dispose();
        pmrem.dispose();
        this.scene.add(new THREE.HemisphereLight('#ffffff', '#b8a68f', 1.4));
        this.light = new THREE.DirectionalLight('#fff6ea', 2.2);
        this.light.castShadow = true;
        // affordable now that the shadow map isn't redrawn every frame
        this.light.shadow.mapSize.set(mobile ? 1024 : 2048, mobile ? 1024 : 2048);
        this.light.shadow.radius = 3;
        this.light.shadow.bias = -0.0004;
        this.light.shadow.normalBias = 0.2;
        this.scene.add(this.light, this.light.target);

        this.camera = new THREE.PerspectiveCamera(30, 1, 1, 10000);
        this.controls = new OrbitControls(this.camera, this.$refs.canvas);
        Object.assign(this.controls, {
            enableDamping: true,
            enablePan: false,
            minAzimuthAngle: -1.2,
            maxAzimuthAngle: 1.2,
            minPolarAngle: 0.45,
            maxPolarAngle: Math.PI - 0.45,
        });
        // OrbitControls forces touch-action none; pan-y lets a vertical swipe scroll the page, a sideways one still orbits
        this.$refs.canvas.style.touchAction = 'pan-y';
        // one finger only orbits sideways (vertical scrolls the page), so two fingers also tilt
        this.controls.touches.TWO = THREE.TOUCH.DOLLY_ROTATE;
        // shown once the first model is on screen, see build()
        this.hintPending = this.touchHint && window.matchMedia('(pointer: coarse)').matches;

        this.explode = 0;
        this.resizeObserver = new ResizeObserver(this.resize);
        this.resizeObserver.observe(this.$refs.container);

        // the label is drawn with the site font
        if (document.fonts) await document.fonts.ready;
        if (!this.renderer) return;
        this.build();
        this.resetCamera();
        this.tick();
    },
    beforeDestroy() {
        cancelAnimationFrame(this.frame);
        cancelAnimationFrame(this.hintFrame);
        clearTimeout(this.hintTimer);
        this.resizeObserver.disconnect();
        this.controls.dispose();
        this.disposeModel();
        this.scene.environment.dispose();
        this.renderer.dispose();
        this.renderer = null;
    },
    // Vue 3 name of the same hook: the backend admin mounts this component too
    beforeUnmount() {
        this.$options.beforeDestroy.call(this);
    },
    methods: {
        // renders only while something moves; an idle 60 fps loop drains and heats phones
        tick() {
            this.frame = requestAnimationFrame(this.tick);
            const target = this.exploded ? 1 : 0;
            const exploding = Math.abs(target - this.explode) > 0.001;
            if (exploding) this.explode += (target - this.explode) * 0.12;
            // rebuilt layers start at z = 0, so a dirty frame re-applies the explode offset too
            if ((exploding || this.dirty) && this.layers) {
                // the backplate stays, every layer moves one gap further than the one behind it
                this.layers.forEach((layer, index) => (layer.position.z = (index + 1) * this.explodeGap * this.explode));
            }
            const moved = this.controls.update();
            if (!moved && !exploding && !this.dirty) return;
            // orbiting alone leaves the shadows untouched
            if (exploding || this.dirty) this.renderer.shadowMap.needsUpdate = true;
            this.dirty = false;
            this.renderer.render(this.scene, this.camera);
        },
        resize() {
            const el = this.$refs.container;
            if (!el || !this.renderer) return;
            const width = el.clientWidth;
            const height = el.clientHeight;
            if (width < 2 || height < 2) return;
            // toolbar show/hide nudges the box by a few px; a new buffer each time kills the phone tab
            const longest = Math.max(width, height);
            const mobile = window.matchMedia('(max-width: 1023.98px)').matches;
            const ratio = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2, (mobile ? 1024 : 2048) / longest);
            const bufW = Math.round(width * ratio);
            const bufH = Math.round(height * ratio);
            if (this.buffer && Math.abs(this.buffer.w - bufW) < 32 && Math.abs(this.buffer.h - bufH) < 32) return;
            this.buffer = { w: bufW, h: bufH };
            this.renderer.setPixelRatio(ratio);
            this.renderer.setSize(width, height, false);
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            this.dirty = true;
        },
        reframe() {
            this.build();
            if (this.renderer) this.resetCamera();
        },
        resetCamera() {
            // the narrow portrait phone stage crops the board sideways, so start further out there
            const mobile = window.matchMedia('(max-width: 1023.98px)').matches;
            const distance = Math.max(this.width, this.height) * (this.cuts?.length ? 2.45 : 3) * (mobile ? 1.35 : 1);
            if (this.cuts?.length) this.camera.position.set(distance * 0.62, distance * 0.32, distance * 0.72);
            else this.camera.position.set(distance * 0.34, distance * 0.14, distance * 0.93);
            this.controls.minDistance = distance * 0.4;
            this.controls.maxDistance = distance * 1.6;
            this.controls.target.set(0, 0, 0);
            this.dirty = true;
        },
        disposeModel() {
            if (!this.model) return;
            this.scene.remove(this.model);
            this.model.traverse((node) => {
                if (!node.isMesh) return;
                node.geometry.dispose();
                const seen = new Set();
                [].concat(node.material).forEach((material) => {
                    if (seen.has(material)) return;
                    seen.add(material);
                    ['map', 'alphaMap', 'bumpMap'].forEach((key) => material[key] && material[key].dispose());
                    material.dispose();
                });
            });
            this.model = null;
        },
        woodMaterial(color, tile = true, shift = 0) {
            const map = woodTexture(color, this.renderer.capabilities.getMaxAnisotropy());
            if (tile) map.repeat.set(this.width / GRAIN_MM, this.height / GRAIN_MM);
            map.offset.set(...grainShift(shift));
            // the grain doubles as relief so fibres catch the raking light
            return new THREE.MeshStandardMaterial({ map, bumpMap: map, bumpScale: GRAIN_BUMP, roughness: 0.8 });
        },
        cutAlpha(source) {
            const texture = new THREE.CanvasTexture(source);
            texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
            // mipmaps + alphaTest sparkle thin streets into noise
            texture.generateMipmaps = false;
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            return texture;
        },
        cutSurface(material, alphaMap) {
            Object.assign(material, { alphaMap, alphaTest: 0.5, alphaToCoverage: true });
            return material;
        },
        // laser engraving burns natural wood darker and shows the lighter wood under a dark stain
        // `soft`: a faint, narrow edge shadow instead of the full scorch, so the face stays clean and light
        faceMaterial(color, mask, engraving, burnPx, shift = 0, soft = false) {
            const canvas = document.createElement('canvas');
            canvas.width = mask.width;
            canvas.height = mask.height;
            const ctx = canvas.getContext('2d');
            const grain = woodCanvas(color);
            const [du, dv] = grainShift(shift);
            for (let y = -dv * grain.height; y < canvas.height; y += grain.height) {
                for (let x = -du * grain.width; x < canvas.width; x += grain.width) ctx.drawImage(grain, x, y);
            }
            const wood = woodAlpha(mask);
            if (burnPx) {
                const holes = holesOf(wood);
                ctx.drawImage(tintedMask(burnRim(wood, burnPx, holes), soft ? 'rgba(18, 10, 6, 0.18)' : 'rgba(18, 10, 6, 0.5)'), 0, 0);
                const edge = soft ? 'rgba(12, 7, 4, 0.4)' : 'rgba(12, 7, 4, 0.82)';
                ctx.drawImage(tintedMask(burnRim(wood, Math.max(2, Math.round(burnPx / 3)), holes), edge), 0, 0);
            }
            if (engraving) {
                const mark = lightness(color) < 0.2 ? 'rgba(222, 200, 165, 0.9)' : 'rgba(58, 30, 12, 0.8)';
                ctx.drawImage(tintedMask(engraving, mark), 0, 0);
            }
            const map = new THREE.CanvasTexture(canvas);
            map.colorSpace = THREE.SRGBColorSpace;
            map.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
            if (!engraving) return new THREE.MeshStandardMaterial({ map, bumpMap: map, bumpScale: GRAIN_BUMP, roughness: 0.8 });

            // recessed where engraved
            const relief = document.createElement('canvas');
            relief.width = engraving.width;
            relief.height = engraving.height;
            const reliefCtx = relief.getContext('2d');
            reliefCtx.fillStyle = '#fff';
            reliefCtx.fillRect(0, 0, relief.width, relief.height);
            reliefCtx.drawImage(tintedMask(engraving, '#000'), 0, 0);
            return new THREE.MeshStandardMaterial({
                map,
                bumpMap: new THREE.CanvasTexture(relief),
                bumpScale: 1.5,
                roughness: 0.85,
            });
        },
        // a laser-cut board from a mask canvas, its top at z = PLY
        // `fine` is the road / letter ply: no face-burn or wall-grow, the strips are too thin
        // face-burn stays on the land ply; sky / letters only char on the inside wall
        // shortcut: stacked planes fake the char wall; extrude the mask if we need a true side surface
        cutLayer(mask, color, engraving, edge = 'broad', shift = 1) {
            const group = new THREE.Group();
            group.userData.mask = mask;
            const geometry = new THREE.PlaneGeometry(this.width, this.height);
            const fine = edge === 'fine';
            const slices = this.sliceCount || SLICES;
            const soft = edge === 'soft';
            const burnPx =
                fine || (engraving && edge !== 'solid' && !soft) ? 0 : Math.max(2, Math.round(mask.width / (soft ? 256 : 64)));
            const wallPx = fine ? 0 : Math.max(1, Math.round(mask.width / 80));
            const faceAlpha = this.cutAlpha(mask);
            const wallAlpha = wallPx ? this.cutAlpha(growMask(mask, wallPx)) : faceAlpha;
            for (let i = 1; i <= slices; i++) {
                const top = i === slices;
                const material = top
                    ? engraving || burnPx
                        ? this.faceMaterial(color, mask, engraving, burnPx, shift, soft)
                        : this.woodMaterial(color, true, shift)
                    : new THREE.MeshStandardMaterial({ color: charred(color), roughness: 1 });
                this.cutSurface(material, top ? faceAlpha : wallAlpha);
                const slice = new THREE.Mesh(geometry, material);
                slice.position.z = (i / slices) * PLY;
                slice.castShadow = top;
                slice.receiveShadow = true;
                group.add(slice);
            }
            return group;
        },
        box(width, height, depth, material) {
            const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);
            mesh.castShadow = mesh.receiveShadow = true;
            return mesh;
        },
        titleFont(px) {
            return this.titleTypo ? fontFromTypo(this.titleTypo, px).font : `600 ${px}px Figtree, Georgia, serif`;
        },
        typoCase(text) {
            return this.titleTypo && fontFromTypo(this.titleTypo, 1).uppercase ? text.toUpperCase() : text;
        },
        // engraved title badge of `wood`; position is up to the caller
        // 0.325 = the old 0.26 at the large size, now the medium
        label(wood, depth, side, text = this.titleText, subtitle = this.subtitle, width = this.width * 0.325 * this.titleSize) {
            const height = width * 0.28;
            const canvas = document.createElement('canvas');
            canvas.width = 1024;
            canvas.height = Math.round(1024 * (height / width));
            const ctx = canvas.getContext('2d');
            ctx.drawImage(woodCanvas(wood), 0, 0, canvas.width, canvas.height);
            // dark engraved text would vanish on walnut and darker woods
            ctx.fillStyle = lightness(wood) < 0.4 ? 'rgba(240, 225, 200, 0.95)' : 'rgba(55, 32, 15, 0.9)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = this.titleFont(canvas.height * 0.36);
            ctx.fillText(text, canvas.width / 2, canvas.height * (subtitle ? 0.42 : 0.5), canvas.width * 0.9);
            if (subtitle) {
                ctx.font = `${canvas.height * 0.14}px Figtree, Georgia, serif`;
                ctx.fillText(subtitle, canvas.width / 2, canvas.height * 0.75, canvas.width * 0.9);
            }
            const face = new THREE.CanvasTexture(canvas);
            face.colorSpace = THREE.SRGBColorSpace;
            const front = new THREE.MeshStandardMaterial({ map: face, roughness: 0.85 });
            const geometry = roundedPlate(width, height, depth, height * 0.12);
            const mesh = new THREE.Mesh(geometry, [front, side]);
            mesh.castShadow = mesh.receiveShadow = true;
            mesh.userData.height = height;
            return mesh;
        },
        // the laminated frame of frameRings.cjs, one PLY per ring from the back lip (under the backplate) up;
        // userData.top is the front face
        // shortcut: rail splits assume the default A4 sheet; pass the admin's sheet size if orders start to differ
        frameModel(wood, stackPlies) {
            const { rings, outerMm } = frameRings({ boardMm: { width: this.width, height: this.height }, stackPlies });
            const group = new THREE.Group();
            const toScene = ([x, y]) => new THREE.Vector2(x - outerMm.width / 2, outerMm.height / 2 - y);
            rings.forEach((ring, k) => {
                const front = k === rings.length - 1;
                // alternate plies a shade apart so the laminations read on the charred edge
                const edge = new THREE.MeshStandardMaterial({ color: charred(wood).offsetHSL(0, 0, k % 2 ? 0.03 : 0), roughness: 1 });
                // cap UVs are board millimetres, so the grain runs on unbroken across the splits and the front reads
                // as solid wood; it only turns at the mitres, the uprights' grain runs along them
                const face = (vertical) => {
                    const material = this.woodMaterial(wood, false);
                    material.map.repeat.set(1 / GRAIN_MM, 1 / GRAIN_MM);
                    material.map.rotation = vertical ? Math.PI / 2 : 0;
                    return material;
                };
                const along = face(false);
                // hidden plies only show their edges, one material will do
                const upright = front ? face(true) : along;
                const faces = { T: along, B: along, L: upright, R: upright };
                ring.rails.forEach((rail) => {
                    const geometry = new THREE.ExtrudeGeometry(new THREE.Shape(rail.ring.map(toScene)), {
                        depth: PLY,
                        bevelEnabled: false,
                    });
                    const mesh = new THREE.Mesh(geometry, [faces[rail.side], edge]);
                    mesh.position.z = (k - 2) * PLY;
                    mesh.castShadow = mesh.receiveShadow = true;
                    group.add(mesh);
                });
            });
            group.userData.top = (rings.length - 2) * PLY;
            return group;
        },
        // two identical flat teardrops, each slotted halfway (one from the top, one from the bottom) so they
        // slide together at 90°; the point stands at z = 0, the optional plaque lies flat in front of it
        pinModel(wood, text) {
            const group = new THREE.Group();
            const centre = PIN_HEIGHT - PIN_RADIUS;
            const tangent = Math.asin(PIN_RADIUS / centre);
            const shape = new THREE.Shape()
                .moveTo(0, 0)
                .lineTo(PIN_RADIUS * Math.cos(tangent), centre - PIN_RADIUS * Math.sin(tangent))
                .absarc(0, centre, PIN_RADIUS, -tangent, Math.PI + tangent, false)
                .lineTo(0, 0);
            const geometry = new THREE.ExtrudeGeometry(shape, { depth: PLY, bevelEnabled: false, curveSegments: 24 });
            geometry.translate(0, 0, -PLY / 2);
            // cap UVs are millimetres
            const face = this.woodMaterial(wood, false);
            face.map.repeat.set(1 / GRAIN_MM, 1 / GRAIN_MM);
            const edge = new THREE.MeshStandardMaterial({ color: charred(wood), roughness: 1 });
            [0, Math.PI / 2].forEach((turn) => {
                const piece = new THREE.Mesh(geometry, [face, edge]);
                // stand the outline up (y → z), then turn the second piece across the first
                piece.rotation.set(Math.PI / 2, 0, turn, 'ZXY');
                piece.castShadow = piece.receiveShadow = true;
                group.add(piece);
            });
            if (text) {
                const plaque = this.label(wood, PLY, edge, this.typoCase(text), '', PIN_LABEL_WIDTH);
                plaque.position.set(0, -PIN_RADIUS - plaque.userData.height / 2, PLY / 2);
                group.add(plaque);
            }
            return group;
        },
        // several props often change in one flush (size + masks + woods); each full rebuild re-blurs every mask
        build() {
            if (this.buildQueued) return;
            this.buildQueued = true;
            this.$nextTick(async () => {
                // canvas text silently falls back if the chosen font file hasn't downloaded yet
                if (this.titleTypo && this.title && document.fonts) {
                    await document.fonts.load(this.titleFont(100), this.title).catch(() => {});
                }
                this.buildQueued = false;
                this.buildNow();
            });
        },
        buildNow() {
            const usingCuts = this.cuts && this.cuts.length;
            if (!this.renderer || !(usingCuts || this.top)) return;
            this.disposeModel();
            const { width: w, height: h, woods } = this;
            const model = new THREE.Group();

            let backMaterial = this.woodMaterial(woods.backplate);
            if (this.backplateEngraving) {
                const side = this.woodMaterial(woods.backplate);
                const face = this.faceMaterial(
                    woods.backplate,
                    solidCanvas(this.backplateEngraving.width, this.backplateEngraving.height),
                    this.backplateEngraving,
                    0
                );
                backMaterial = [side, side, side, side, face, side];
            }
            const backplate = this.box(w, h, PLY, backMaterial);
            backplate.position.z = -PLY / 2;
            const cut = usingCuts
                ? this.cuts.map((layer, index) =>
                      this.cutLayer(layer.mask, layer.color, layer.engraving || null, layer.edge || 'solid', index + 1)
                  )
                : [
                      this.middle && this.cutLayer(this.middle, woods.middle, this.middleEngraving, 'soft', 1),
                      this.cutLayer(this.top, woods.top, this.engraving, this.middle ? 'fine' : 'broad', 2),
                      this.overlay && this.cutLayer(this.overlay, woods.overlay || woods.top, null, 'fine', 3),
                  ].filter(Boolean);
            cut.forEach((layer, index) => {
                const level = usingCuts && this.cuts[index].z != null ? this.cuts[index].z : index;
                layer.translateZ(level * PLY);
                layer.userData.top = (level + 1) * PLY;
            });
            // plaque stays on the engraved top ply, even when an overlay sits in front of it
            const topIndex = usingCuts ? cut.length - 1 : this.middle ? 1 : 0;
            const labelLayer = new THREE.Group();
            if (this.title && !this.labelOnFrame) {
                const edge = new THREE.MeshStandardMaterial({ color: charred(woods.top), roughness: 1 });
                const plaque = this.label(woods.top, PLY, edge);
                plaque.position.set(0, -h / 2 + plaque.userData.height * 1.1, PLY / 2);
                labelLayer.add(plaque);
            }
            labelLayer.translateZ(topIndex * PLY);
            // wrapped so the explode offset doesn't fight the stacking offset
            this.layers = cut.map((layer) => new THREE.Group().add(layer));
            if (this.layers[topIndex]) this.layers[topIndex].add(labelLayer);
            // stands on the highest ply with wood under the centre (topo plies don't all reach it, roads leave gaps)
            // and rides with that ply when exploded; with none it stands on the backplate
            if (this.pin) {
                const pin = this.pinModel(woods.frame, this.pin.text);
                let under = -1;
                cut.forEach((layer, index) => {
                    if (woodAtCentre(layer.userData.mask) && (under < 0 || layer.userData.top > cut[under].userData.top)) {
                        under = index;
                    }
                });
                if (under < 0) model.add(pin);
                else {
                    pin.position.z = cut[under].userData.top;
                    this.layers[under].add(pin);
                }
            }
            model.add(backplate, ...this.layers);

            // backplate + every cut level, so the frame is as deep as the one that will be cut
            const stackPlies = 1 + Math.max(...cut.map((layer) => Math.round(layer.userData.top / PLY)));
            const frame = this.frameModel(woods.frame, stackPlies);
            model.add(frame);
            // an island cut in one piece with the frame's top ply, so it can grow past the rail's width
            if (this.title && this.labelOnFrame) {
                const islandSide = this.woodMaterial(woods.frame, false);
                const island = this.label(woods.frame, PLY, islandSide);
                island.position.set(0, -h / 2 + island.userData.height / 2, frame.userData.top - PLY / 2);
                model.add(island);
            }

            const wall = new THREE.Mesh(
                new THREE.PlaneGeometry(w * 8, h * 8),
                new THREE.MeshStandardMaterial({ color: WALL_COLOR, roughness: 1 })
            );
            // behind the back lip
            wall.position.z = -2 * PLY - 0.5;
            wall.receiveShadow = true;
            model.add(wall);

            const reach = Math.max(w, h) * 0.9;
            this.light.position.set(-reach * 0.6, reach * 0.9, reach * 1.4);
            Object.assign(this.light.shadow.camera, {
                left: -reach,
                right: reach,
                top: reach,
                bottom: -reach,
                far: reach * 5,
            });
            this.light.shadow.camera.updateProjectionMatrix();

            // the room only adds soft directional shading; the lights keep the original brightness
            model.traverse((node) => node.isMesh && [].concat(node.material).forEach((m) => (m.envMapIntensity = ENV_INTENSITY)));
            this.model = model;
            this.scene.add(model);
            this.dirty = true;
            if (this.hintPending) {
                this.hintPending = false;
                // tick() renders the model in the next frame; the hint follows it
                this.hintFrame = requestAnimationFrame(() => {
                    this.hintVisible = true;
                    this.hintTimer = setTimeout(() => (this.hintVisible = false), 4000);
                });
            }
        },
    },
};
</script>

<style scoped>
/* fingertip swipes sideways, mirroring the gesture that orbits the model */
.hint-hand {
    animation: hint-swipe 1.6s ease-in-out infinite;
}

@keyframes hint-swipe {
    0%,
    100% {
        transform: translateX(-32px);
    }
    50% {
        transform: translateX(32px);
    }
}
</style>
